<template lang="pug">
  v-dialog(v-model="showNewBillingDialog"
    :fullscreen="$vuetify.breakpoint.mdAndDown"
    max-width="800"
    persistent)
    v-snackbar( v-model="snackbar" :color="snackbarColor" ) {{ snackbarMessage }}
      template( v-slot:action="{attrs}")
        v-btn(text v-bind="attrs"
          @click="errorSnackbar = false") {{ $t('buttons.closeButtonLabel') }}
    v-form.fill-height(v-model="valid" ref="form" @submit.prevent="onNextButtonSubmit" lazy-validation)
      v-stepper(v-model="actualStep")
        v-stepper-header
          v-stepper-step(:complete="actualStep > 1" complete-icon="fal fa-check"
            step="1") {{ $t('newBillingDialog.firstStepEnterParametersTitle') }}
          v-divider
          v-stepper-step(:complete="actualStep > 2" complete-icon="fal fa-check"
            step="2") {{ $t('newBillingDialog.secondStepControlTitle') }}
          v-divider
          v-stepper-step(step="3") {{ $t('newBillingDialog.thirdStepStatisticsTitle') }}
        v-stepper-items
          v-stepper-content.pb-0(step="1")
            v-card(flat)
              v-card-text
                v-text-field( v-model="billingName"
                  outlined
                  :rules="nameRules"
                  :label=" $t('newBillingDialog.billingNameTextFieldLabel') ")
                v-select( v-model="format"
                  :items="formats"
                  :rules="selectRules"
                  :loading="fetchInProgress"
                  :disabled="fetchInProgress"
                  outlined
                  return-object
                  :label=" $t('newBillingDialog.formatVSelectLabel') "
                  append-icon="fal fa-chevron-down")
                v-menu(ref="menuFrom"
                  v-model="menuFrom"
                  :close-on-content-click="false"
                  :return-value.sync="dateFrom"
                  transition="scale-transition"
                  offset-y
                  min-width="auto")
                  template( v-slot:activator="{ on, attrs }")
                    v-text-field(v-model="formattedDateFrom"
                      chips
                      small-chips
                      :label=" $t('newBillingDialog.fromDayPickerLabel') "
                      prepend-icon="fas fa-calendar-alt"
                      readonly
                      v-bind="attrs"
                      v-on="on")
                  v-date-picker(v-model="dateFrom"
                    @change="saveFrom(dateFrom)"
                    no-title
                    scrollable
                    first-day-of-week="1"
                    show-week
                    prev-icon="fas fa-chevron-left"
                    next-icon="fas fa-chevron-right")
                    v-spacer
                    v-btn(text
                      color="primary"
                      @click="menuFrom = false") {{ $t('buttons.cancelButtonLabel') }}
                v-menu(ref="menuTo"
                  v-model="menuTo"
                  :close-on-content-click="false"
                  :return-value.sync="dateTo"
                  transition="scale-transition"
                  offset-y
                  min-width="auto")
                  template( v-slot:activator="{ on, attrs }")
                    v-text-field(v-model="formattedDateTo"
                      multiple
                      chips
                      small-chips
                      :label=" $t('newBillingDialog.toDayPickerLabel') "
                      prepend-icon="fas fa-calendar-alt"
                      readonly
                      v-bind="attrs"
                      v-on="on")
                  v-date-picker(v-model="dateTo"
                    @change="saveTo(dateTo)"
                    no-title
                    scrollable
                    first-day-of-week="1"
                    show-week
                    prev-icon="fas fa-chevron-left"
                    next-icon="fas fa-chevron-right")
                    v-spacer
                    v-btn(text
                      color="primary"
                      @click="menuTo = false") {{ $t('buttons.cancelButtonLabel') }}
              v-divider
              v-card-actions.justify-end
                v-btn( @click="onCloseButtonClick" :disabled="fetchInProgress" text color="primary" ) {{ $t('buttons.closeButtonLabel') }}
                v-btn( type="submit" :disabled="fetchInProgress || !valid" color="primary") {{ $t('buttons.nextButtonLabel') }}
          v-stepper-content.pb-0(step="2")
            v-card(flat height="397").d-flex.flex-column
              v-container.flex-shrink-0.flex-grow-1.d-flex.flex-column
                v-card-text.text-body-1
                  v-list-item {{ $t('newBillingDialog.billingNameListItem') }}
                    v-list-item-content.font-weight-black.pl-2 {{ billingName }}
                  v-list-item {{ $t('newBillingDialog.chosenFormatListItem') }}
                    v-list-item-content.font-weight-black.pl-2 {{ chosenFormat }}
                  v-list-item {{ $t('newBillingDialog.chosenPeriodListItem') }}
                    v-list-item-content.font-weight-black.pl-2 {{ chosenPeriod }}
              v-divider
              v-card-actions.justify-end.align-end
                v-btn( @click="onStepBackButtonClick")
                  v-icon( small left ) fas fa-arrow-alt-left
                  span.mr-1 {{ $t('buttons.stepBackButtonLabel') }}
                v-spacer
                v-btn( @click="onCloseButtonClick" :disabled="saveInProgress" text color="primary" ) {{ $t('buttons.closeButtonLabel') }}
                v-btn( @click="onGetNewBillingButtonClick" :disabled="saveInProgress" :loading="saveInProgress" color="primary") {{ $t('newBillingDialog.getNewBillingButtonLabel') }}
          v-stepper-content.pb-0(step="3")
            v-card(flat height="397").d-flex.flex-column
              v-container.flex-shrink-0.flex-grow-1
                v-card-text.text-body-1.d-flex.flex-column.align-center
                    v-list-item 198
                      v-list-item-content.font-weight-black.pl-2 {{ $t('newBillingDialog.newBillingsCount') }}
                    v-list-item {{ $t('newBillingDialog.totalPrice') }}
                      v-list-item-content.font-weight-black.pl-2 3230,00€
              v-divider
              v-card-actions.justify-end.align-end
                v-btn( @click="onFinishButtonClick" color="primary") {{ $t('buttons.finishButtonLabel') }}
</template>

<script lang="ts">
import Vue from "vue";
import {Component, Prop} from "vue-property-decorator";
import moment from "moment";
import i18n from "@/plugins/i18n";

@Component({
  components: {}
})

export default class NewBillingDialog extends Vue {
  $refs!: {
    menuFrom: Vue & {
      save(date: string): void,
    },
    menuTo: Vue & {
      save(date: string): void,
    },
    form: Vue & {
      validate(): boolean,
      resetValidation(): boolean
    }
  }

  @Prop({type: Boolean, default: false}) showNewBillingDialog!: boolean

  private actualStep: number = 1

  private fetchInProgress: boolean = false
  private saveInProgress: boolean = false

  private snackbar: boolean = false
  private snackbarMessage: string = ''
  private snackbarColor: string = ''

  private format: string = ''
  // noinspection JSMismatchedCollectionQueryUpdate
  private formats: string[] = []

  private name: string = ''
  private dateFrom: string = ''
  private dateTo: string = ''
  private menuFrom: boolean = false
  private menuTo: boolean = false
  private formattedDateFrom: string = ''
  private formattedDateTo: string = ''

  private billingWasCreated: boolean = false

  private valid: boolean = false
  private nameRules: Array<(v:string) => string | boolean> = [
    (v:string) => !!v || i18n.t('newBillingDialog.nameValidationText').toString()
  ]
  private selectRules: Array<(v: string) => string | boolean> = [
    (v: string) => !!v || i18n.t('newBillingDialog.selectValidationText').toString()
  ]

  get billingName(): string {
    return this.$t('newBillingDialog.billingName').toString() + ', ' + this.name
  }

  get chosenFormat(): string {
    return this.format
  }

  get chosenPeriod(): string {
    if (moment(this.dateFrom).toDate().getFullYear() === moment(this.dateTo).toDate().getFullYear()) {
      return moment(this.dateFrom).format('D.M.') + ' - ' + moment(this.dateTo).format('D.M.YYYY')
    } else {
      return moment(this.dateFrom).format('D.M.YYYY') + ' - ' + moment(this.dateTo).format('D.M.YYYY')
    }
  }

  async fetchFormats(): Promise<void> {

    this.fetchInProgress = true

    try {
      this.formats = ["PDF", "XML", "XLS", "DOCX"]
    } catch (e) {
      this.snackbar = true
      this.snackbarMessage = this.$t('errors.fetchDataError').toString()
      this.snackbarColor = 'error'
    } finally {
      this.fetchInProgress = false
    }
  }

  async getNewBilling(): Promise<void> {

    this.saveInProgress = true

    try {
      this.billingWasCreated = true
      this.snackbar = true
      this.snackbarColor = 'success'
      this.snackbarMessage = this.$t('success.billingCreatedSuccess').toString()
    } catch (e) {
      this.snackbar = true
      this.snackbarMessage = this.$t('errors.saveErrorMessage').toString()
      this.snackbarColor = 'error'
    } finally {
      this.saveInProgress = false
      this.moveToStatisticsStep()
    }
  }

  onStepBackButtonClick(): void {
    this.stepBack()
  }

  stepBack(): void {
    if (this.actualStep === 2) {
      this.actualStep = 1
    }/* else if (this.actualStep === 3) {
      this.actualStep = 2
    }*/
  }

  saveFrom(date: string): void {
    this.$refs.menuFrom.save(date)
    this.getFormattedDateFrom(date)
    this.getMonthName(date)
  }

  saveTo(date: string): void {
    this.$refs.menuTo.save(date)
    this.getFormattedDateTo(date)
    this.getMonthName(date)
  }

  onNextButtonSubmit(): void {
    if (this.$refs.form.validate()) {
      if (this.dateFrom != '' && this.dateTo != '') {
        this.moveToControlStep()
      } else {
        this.snackbar = true
        this.snackbarColor = 'error'
        this.snackbarMessage = this.$t('newBillingDialog.noDaysPicked').toString()
      }
    }
  }

  moveToControlStep(): void {
    this.actualStep = 2
  }

  onGetNewBillingButtonClick(): void {
    this.getNewBilling()
  }

  moveToStatisticsStep(): void {
    this.actualStep = 3
  }

  onFinishButtonClick(): void {
    this.finishAddingNewBilling()
  }

  finishAddingNewBilling(): void {
    this.closeDialog()
  }

  onCloseButtonClick(): void {
    this.closeDialog()
  }

  closeDialog(): void {
    this.$emit('closeDialog', this.billingWasCreated)
  }

  getMonthName(date?: string): string {
    if (date) {
      if (moment(this.dateFrom).month() == moment(this.dateTo).month()) {
        if (moment(this.dateFrom).year() == moment(this.dateTo).year()) {
          return this.name = moment(this.dateFrom).toDate().toLocaleString('sk-SK', {month: 'long', year: 'numeric'})
        } else {
          return this.name = moment(this.dateFrom).toDate().toLocaleString('sk-SK', {month: 'long', year: 'numeric'}) + ' - ' + moment(this.dateTo).toDate().toLocaleString('sk-SK', {month: 'long', year: 'numeric'})
        }
      } else if (moment(this.dateFrom).year() == moment(this.dateTo).year()) {
        return this.name = moment(this.dateFrom).toDate().toLocaleString('sk-SK', {month: 'long'}) + ' - ' + moment(this.dateTo).toDate().toLocaleString('sk-SK', {month: 'long', year: 'numeric'})
      } else {
        return this.name = moment(this.dateFrom).toDate().toLocaleString('sk-SK', {month: 'long', year: 'numeric'}) + ' - ' + moment(this.dateTo).toDate().toLocaleString('sk-SK', {month: 'long', year: 'numeric'})
      }
    } else {
      const thisMonth = new Date()
      thisMonth.setMonth(thisMonth.getMonth()-1)
      return thisMonth.toLocaleString('sk-SK', {month: 'long', year: 'numeric'})
    }
  }

  getFormattedDateFrom(date? :string): string {
    if (date) {
      return this.formattedDateFrom = moment(date).format('D.M.YYYY')
    } else {
      return this.formattedDateFrom = moment(this.dateFrom).format('D.M.YYYY')
    }
  }

  getFormattedDateTo(date?: string): string {
    if (date) {
      return this.formattedDateTo = moment(date).format('D.M.YYYY')
    } else {
      return this.formattedDateTo = moment(this.dateTo).format('D.M.YYYY')
    }
  }

  mounted(): void {
    this.dateFrom = moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD').toString()
    this.getFormattedDateFrom()
    this.dateTo = moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD').toString()
    this.getFormattedDateTo()
    this.name = this.getMonthName()
    this.fetchFormats()
  }
}
</script>

<style lang="stylus" scoped>

</style>